import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// A list's page number lives in the URL PATH and its filters in the QUERY STRING, so opening a
// row and coming back (browser Back, a Back button, or a link) lands on the same page with the
// same filters - nothing is kept in component state that a remount would lose.
//
//   /hubs                      page 1, default filters
//   /hubs/page/3?status=true   page 3, Active only
//
// Drop-in for the two useState calls every list had:
//   const [page, setPage] = useState(1);              ->  const { page, setPage, filters, setFilters } =
//   const [filters, setFilters] = useState(INITIAL);  ->      useListParams(INITIAL_FILTERS);
// setPage / setFilters accept a value or an updater function, like useState setters, and several
// calls in one event (e.g. setPage(1) then setFilters(...)) become ONE history entry.
//
// Options
//   zeroBased   page is 0-based for this list's API (the URL still shows 1-based numbers).
//   pathStyle   "page" (default) -> /list/page/N;  "segment" -> /list/N (the car list's existing URLs).
//   query       true for a list embedded in another page (a detail page's tab): the page goes in
//               the query too, under `${prefix}page`, so the host page's own URL is untouched.
//   prefix      query-key prefix for embedded lists, so two lists on one page don't collide.
// Only values that differ from the initial filters are written, so a clean list has a clean URL.

const TEXT_KEYS = new Set(["search", "searchKey", "q"]);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const encodeValue = (value) => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? "" : value.toISOString();
    if (Array.isArray(value)) return value.map(encodeValue).join(",");
    if (value && typeof value === "object") return JSON.stringify(value);
    return String(value ?? "");
};

const decodeValue = (raw, initial, key) => {
    if (Array.isArray(initial)) return raw === "" ? [] : raw.split(",");
    if (typeof initial === "number") {
        const n = Number(raw);
        return raw !== "" && Number.isFinite(n) ? n : initial;
    }
    if (typeof initial === "boolean") return raw === "true" ? true : raw === "false" ? false : initial;
    if (initial && typeof initial === "object" && !(initial instanceof Date)) {
        try { return JSON.parse(raw); } catch (error) { return initial; }
    }
    if (TEXT_KEYS.has(key)) return raw;
    if (raw === "true") return true;
    if (raw === "false") return false;
    if (ISO_DATE.test(raw)) {
        const date = new Date(raw);
        return Number.isNaN(date.getTime()) ? raw : date;
    }
    return raw;
};

const PAGE_TAIL = { page: /\/page\/\d+\/?$/, segment: /\/\d+\/?$/ };

// Each list remembers its last URL (page + filters) for this browser tab, so a detail page's
// "Back to ..." button can return to exactly where the user was: navigate(listBackPath("/hubs")).
const memoryKey = (base) => `list-url:${base}`;
export const listBackPath = (base) => {
    try {
        return window.sessionStorage.getItem(memoryKey(base)) || base;
    } catch (error) {
        return base;
    }
};

const useListParams = (initialFilters = {}, options = {}) => {
    const { zeroBased = false, pathStyle = "page", query = false, prefix = "", pageParam = "pageNo" } = options;
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Callers often build initialFilters inline; key everything off its content, not its identity.
    const initialKey = JSON.stringify(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initial = useMemo(() => initialFilters, [initialKey]);

    const pageKey = `${prefix}page`;
    const search = location.search;

    const page1 = useMemo(() => {
        const raw = query ? new URLSearchParams(search).get(pageKey) : params[pageParam];
        const n = parseInt(raw, 10);
        return Number.isFinite(n) && n > 0 ? n : 1;
    }, [query, search, pageKey, params, pageParam]);

    const filters = useMemo(() => {
        const qs = new URLSearchParams(search);
        const out = { ...initial };
        Object.keys(initial).forEach((key) => {
            const raw = qs.get(`${prefix}${key}`);
            if (raw !== null) out[key] = decodeValue(raw, initial[key], key);
        });
        return out;
    }, [search, initial, prefix]);

    const basePath = useMemo(() => (query ? location.pathname : location.pathname.replace(PAGE_TAIL[pathStyle] || PAGE_TAIL.page, "") || "/"), [query, location.pathname, pathStyle]);

    // The latest wanted state, so several setter calls in one event build on each other.
    const pending = useRef(null);
    // What was last sent to the router: a second click that lands before the re-render must build
    // on it, not on the URL the page is still showing.
    const sent = useRef(null);
    const scheduled = useRef(false);
    const pushRef = useRef(false);
    const live = useRef({ page1, filters, search, basePath, pathname: location.pathname });
    live.current = { page1, filters, search, basePath, pathname: location.pathname };

    const flush = useCallback(() => {
        scheduled.current = false;
        const want = pending.current;
        pending.current = null;
        if (!want) return;
        const { basePath: base, search: currentSearch } = live.current;
        const qs = new URLSearchParams(currentSearch);
        // Rewrite only this list's keys; anything else in the query (a tab, another list) is kept.
        Object.keys(initial).forEach((key) => qs.delete(`${prefix}${key}`));
        qs.delete(pageKey);
        Object.keys(initial).forEach((key) => {
            const value = want.filters[key];
            if (value === undefined || value === null) return;
            const text = encodeValue(value);
            if (text === encodeValue(initial[key])) return;
            qs.set(`${prefix}${key}`, text);
        });
        let pathname = base;
        if (want.page1 > 1) {
            if (query) qs.set(pageKey, String(want.page1));
            else pathname = pathStyle === "segment" ? `${base.replace(/\/$/, "")}/${want.page1}` : `${base.replace(/\/$/, "")}/page/${want.page1}`;
        }
        const nextSearch = qs.toString();
        const push = pushRef.current;
        pushRef.current = false;
        const target = `${pathname}${nextSearch ? `?${nextSearch}` : ""}`;
        if (target === `${live.current.pathname}${currentSearch || ""}`) return;
        sent.current = { page1: want.page1, filters: want.filters };
        // Page changes are history steps (Back goes to the previous page); filter edits replace the
        // entry so typing in a search box doesn't leave a trail of Back presses.
        navigate({ pathname, search: nextSearch ? `?${nextSearch}` : "" }, { replace: !push, preventScrollReset: true });
    }, [initial, prefix, pageKey, query, pathStyle, navigate]);

    const update = useCallback((change) => {
        const base = pending.current || sent.current || { page1: live.current.page1, filters: live.current.filters };
        pending.current = change(base);
        if (!scheduled.current) {
            scheduled.current = true;
            Promise.resolve().then(flush);
        }
    }, [flush]);

    const toApi = (p1) => (zeroBased ? p1 - 1 : p1);
    const fromApi = (p) => (zeroBased ? Number(p) + 1 : Number(p));

    const setPage = useCallback((next) => {
        update((state) => {
            const value = typeof next === "function" ? next(toApi(state.page1)) : next;
            const p1 = Math.max(1, Math.floor(fromApi(value)) || 1);
            if (p1 !== state.page1) pushRef.current = true;
            return { ...state, page1: p1 };
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [update, zeroBased]);

    const setFilters = useCallback((next) => {
        update((state) => ({ ...state, filters: typeof next === "function" ? next(state.filters) : { ...next } }));
    }, [update]);

    // One filter at a time, back to page 1 (what every list's onFilterChange did by hand).
    const setFilter = useCallback((key, value, { keepPage = false } = {}) => {
        update((state) => ({ page1: keepPage ? state.page1 : 1, filters: { ...state.filters, [key]: value } }));
    }, [update]);

    const resetFilters = useCallback(() => {
        update(() => ({ page1: 1, filters: { ...initial } }));
    }, [update, initial]);

    useEffect(() => {
        if (query) return;
        try {
            window.sessionStorage.setItem(memoryKey(basePath), `${location.pathname}${location.search}`);
        } catch (error) { /* private mode - Back still works through browser history */ }
    }, [query, basePath, location.pathname, location.search]);

    // Once the URL has moved, it is the truth again.
    useEffect(() => { sent.current = null; }, [location.key]);
    useEffect(() => () => { pending.current = null; }, []);

    return { page: toApi(page1), setPage, filters, setFilters, setFilter, resetFilters };
};

export default useListParams;
